import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import { getIsProd } from "../config";

export type CustomerVerificationStep =
    | "CustomerHandoff"
    | "CustomerPhoneInput"
    | "CustomerNameSelect"
    | "VerificationComplete";

export type CustomerVerificationInitialStep = "CustomerHandoff" | "CustomerPhoneInput";

const defaultInitialStep: CustomerVerificationStep = "CustomerPhoneInput";

interface VerifiedCustomerState {
    stepHistory: CustomerVerificationStep[];
    currentStep: CustomerVerificationStep;
}

interface VerifiedCustomerStore {
    verifiedCustomers: Record<string, VerifiedCustomerState>;
    getCurrentStep: (sessionId: string) => CustomerVerificationStep;
    getStepHistory: (sessionId: string) => CustomerVerificationStep[];
    pushStep: (sessionId: string, step: CustomerVerificationStep) => void;
    popStep: (sessionId: string) => void;
    resetSteps: (sessionId: string) => void;
    setInitialStep: (sessionId: string, initialStep: CustomerVerificationInitialStep) => void;
}

export const useVerificationFlowStore = createWithEqualityFn<VerifiedCustomerStore>()(
    persist(
        immer(
            devtools(
                (set, get) => ({
                    verifiedCustomers: {},
                    getCurrentStep: (sessionId: string) =>
                        get().verifiedCustomers[sessionId]?.currentStep ?? defaultInitialStep,
                    getStepHistory: (sessionId: string) =>
                        get().verifiedCustomers[sessionId]?.stepHistory ?? [defaultInitialStep],
                    pushStep: (sessionId: string, step: CustomerVerificationStep) => {
                        set(
                            (state) => {
                                if (!state.verifiedCustomers[sessionId]) {
                                    state.verifiedCustomers[sessionId] = {
                                        stepHistory: [defaultInitialStep],
                                        currentStep: defaultInitialStep,
                                    };
                                }
                                state.verifiedCustomers[sessionId].stepHistory.push(step);
                                state.verifiedCustomers[sessionId].currentStep = step;
                            },
                            false,
                            "pushStep",
                        );
                    },
                    popStep: (sessionId: string) => {
                        set(
                            (state) => {
                                const customer = state.verifiedCustomers[sessionId];
                                if (customer && customer.stepHistory.length > 1) {
                                    customer.stepHistory.pop();
                                    customer.currentStep = customer.stepHistory[customer.stepHistory.length - 1];
                                }
                            },
                            false,
                            "popStep",
                        );
                    },
                    resetSteps: (sessionId: string) => {
                        set(
                            (state) => {
                                delete state.verifiedCustomers[sessionId];
                            },
                            false,
                            "resetSteps",
                        );
                    },
                    setInitialStep: (sessionId: string, initialStep: CustomerVerificationInitialStep) =>
                        set(
                            (state) => {
                                const existing = state.verifiedCustomers[sessionId];
                                if (existing) {
                                    return;
                                }
                                state.verifiedCustomers[sessionId] = {
                                    stepHistory: [initialStep],
                                    currentStep: initialStep,
                                };
                            },
                            false,
                            "setInitialStep",
                        ),
                }),
                {
                    enabled: !getIsProd(),
                    store: "verification-flow-store",
                    name: "workspace/customer-panel",
                },
            ),
        ),
        { name: "verification-flow-store", skipHydration: true },
    ),
    shallow,
);
